import { Link } from "react-router-dom";
import { Search, UserMinus, UserCheck, ShieldCheck, ArrowRight, Users, MessageSquare, ClipboardList, Lock, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const HERO_LOGO = "https://customer-assets.emergentagent.com/job_reencuentro-mvp/artifacts/qsbn2fc7_-Version%20lineal.png";

export default function Home() {
  const { user, isAdmin } = useAuth();

  return (
    <div className="space-y-12">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl bg-white border border-[#E5E7EB] shadow-sm">
        <div className="absolute top-0 left-0 right-0 h-1.5 flex">
          <span className="flex-1 bg-[#003893]" />
          <span className="flex-1 bg-[#FFD100]" />
          <span className="flex-1 bg-[#CE1126]" />
        </div>
        <div className="px-6 sm:px-10 py-10 sm:py-14 text-center flex flex-col items-center">
          <img src={HERO_LOGO} alt="Reencuentro" className="h-16 sm:h-24 w-auto" data-testid="home-hero-logo" />
          <p className="mt-5 text-base sm:text-lg font-semibold text-[#003893]">
            Plataforma de reunificación familiar post-desastre
          </p>
          <p className="mt-3 text-sm sm:text-base text-[#475569] leading-relaxed max-w-2xl">
            Reporta, busca y comparte información sobre personas desaparecidas o encontradas con vida tras un desastre natural, protegiendo siempre los datos sensibles.
          </p>

          {/* Main actions */}
          <div className="mt-8 w-full max-w-3xl grid gap-3 sm:grid-cols-3" data-testid="home-actions">
            <Link to="/buscar" data-testid="home-search-btn"
              className="group flex flex-col items-center justify-center gap-2 px-5 py-5 rounded-2xl bg-[#003893] text-white font-semibold hover:bg-[#002a6e] transition-colors shadow-sm">
              <Search className="w-6 h-6" />
              <span>Buscar persona</span>
            </Link>
            <Link to="/reportar/desaparecida" data-testid="home-report-missing-btn"
              className="group flex flex-col items-center justify-center gap-2 px-5 py-5 rounded-2xl bg-white border-2 border-[#CE1126] text-[#CE1126] font-semibold hover:bg-[#CE1126] hover:text-white transition-colors">
              <UserMinus className="w-6 h-6" />
              <span>Reportar desaparecida</span>
            </Link>
            <Link to="/reportar/encontrada" data-testid="home-report-found-btn"
              className="group flex flex-col items-center justify-center gap-2 px-5 py-5 rounded-2xl bg-white border-2 border-[#003893] text-[#003893] font-semibold hover:bg-[#003893] hover:text-white transition-colors">
              <UserCheck className="w-6 h-6" />
              <span>Reportar encontrada</span>
            </Link>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm" data-testid="home-lookup-links">
            <Link to="/consultar-reporte" data-testid="home-lookup-report" className="text-[#003893] font-medium underline hover:text-[#002a6e]">Consultar mi reporte</Link>
            <span className="text-[#CBD5E1]">·</span>
            <Link to="/consultar-solicitud" data-testid="home-lookup-request" className="text-[#003893] font-medium underline hover:text-[#002a6e]">Consultar solicitud</Link>
          </div>

          {user && (
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-sm" data-testid="home-user-links">
              {isAdmin && <Link to="/admin" className="text-[#003893] font-semibold underline hover:text-[#002a6e]">Panel Admin</Link>}
              <Link to="/mis-reportes" className="text-[#003893] font-semibold underline hover:text-[#002a6e]">Mis reportes</Link>
            </div>
          )}
        </div>
      </section>

      {/* Privacy notice */}
      <section className="flex gap-3 p-5 rounded-2xl bg-[#FFF9DB] border border-[#FFE873]" data-testid="home-privacy-notice">
        <ShieldCheck className="w-6 h-6 text-[#8A6D00] shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-[#7A5E00]">Publicar rápido, mostrar poco, proteger el contacto.</p>
          <p className="text-sm text-[#8A6D00] leading-relaxed mt-0.5">
            Nunca exponemos públicamente datos sensibles como cédula, dirección exacta, teléfono, correo ni información médica. El contacto siempre se mantiene privado.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section id="home-how-it-works" className="scroll-mt-20 space-y-5" data-testid="home-how-it-works">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-xl bg-[#003893]/10 flex items-center justify-center"><ClipboardList className="w-5 h-5 text-[#003893]" /></span>
          <h2 className="font-heading text-2xl font-bold text-[#0F172A]">¿Cómo funciona?</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <StepCard n="1" color="#003893" title="Reporta o busca" text="Puedes reportar sin crear cuenta, siempre con un contacto privado, o buscar entre las fichas publicadas." />
          <StepCard n="2" color="#FFD100" dark title="Revisión por riesgo" text="Los casos de bajo riesgo se publican como ficha limitada. Casos con menores o datos sensibles van a revisión del equipo autorizado." />
          <StepCard n="3" color="#CE1126" title="Reunificación" text="El contacto se realiza mediante formularios protegidos dentro de cada ficha, sin exponer información privada." />
        </div>
        <ul className="text-sm text-[#475569] space-y-2 leading-relaxed bg-white rounded-2xl border border-[#E5E7EB] p-5">
          <li className="flex gap-2"><RefreshCw className="w-4 h-4 text-[#003893] shrink-0 mt-0.5" /> Sin conexión, puedes preparar y guardar el reporte como borrador en el dispositivo. Al recuperar conexión podrás enviarlo para registrarlo y publicarlo según las reglas de privacidad.</li>
          <li className="flex gap-2"><Lock className="w-4 h-4 text-[#003893] shrink-0 mt-0.5" /> Para corregir o gestionar un reporte debes iniciar sesión o solicitar la corrección desde la ficha.</li>
        </ul>
      </section>

      {/* Nosotros */}
      <section id="home-about" className="scroll-mt-20 rounded-2xl bg-[#003893] text-white p-7 sm:p-9 space-y-3" data-testid="home-about">
        <h2 className="font-heading text-2xl font-bold flex items-center gap-3">
          <Users className="w-6 h-6 text-[#FFD100]" /> Nosotros
        </h2>
        <p className="text-sm sm:text-base text-white/90 leading-relaxed max-w-3xl">
          Reencuentro es una herramienta humanitaria, privada y gratuita diseñada para ayudar a familias y comunidades a reportar y encontrar personas desaparecidas o encontradas con vida después de un desastre natural. Su propósito es facilitar la reunificación sin exponer información sensible.
        </p>
        <p className="text-xs text-[#FFD100] font-medium pt-1">
          No es un sistema gubernamental, policial, médico ni forense.
        </p>
      </section>

      {/* Contáctanos */}
      <section id="home-contact" className="scroll-mt-20 rounded-2xl border border-[#E5E7EB] bg-white p-7 sm:p-9 space-y-4" data-testid="home-contact">
        <h2 className="font-heading text-2xl font-bold text-[#0F172A] flex items-center gap-3">
          <MessageSquare className="w-6 h-6 text-[#003893]" /> Contáctanos
        </h2>
        <p className="text-sm sm:text-base text-[#475569] leading-relaxed max-w-3xl">
          ¿Encontraste una falla, un problema de privacidad o necesitas comunicarte con el equipo de Reencuentro?
        </p>
        <Link to="/contacto" data-testid="home-contact-btn"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#003893] text-white font-semibold hover:bg-[#002a6e] transition-colors">
          <MessageSquare className="w-4 h-4" /> Contactar al equipo
        </Link>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E5E7EB] pt-6 space-y-3" data-testid="home-footer">
        <div className="flex items-center gap-2">
          <span className="flex h-3 w-10 overflow-hidden rounded-full">
            <span className="flex-1 bg-[#003893]" /><span className="flex-1 bg-[#FFD100]" /><span className="flex-1 bg-[#CE1126]" />
          </span>
          <span className="font-heading font-bold text-[#003893]">Reencuentro</span>
        </div>
        <div className="flex gap-4 text-sm text-[#475569]">
          <Link to="/contacto" data-testid="home-footer-contact-link" className="underline hover:text-[#003893]">Contáctanos</Link>
          <Link to="/privacidad" data-testid="home-privacy-link" className="underline hover:text-[#003893]">Privacidad</Link>
          <Link to="/terminos" data-testid="home-terms-link" className="underline hover:text-[#003893]">Términos de uso</Link>
        </div>
        <p className="text-xs text-[#94A3B8] max-w-2xl">Reencuentro no es un sistema gubernamental, policial, médico ni forense. Es una iniciativa humanitaria, privada y gratuita.</p>
      </footer>
    </div>
  );
}

function StepCard({ n, color, title, text, dark }) {
  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5 space-y-2.5">
      <span className="flex w-9 h-9 rounded-full items-center justify-center font-bold text-sm"
        style={{ backgroundColor: color, color: dark ? "#0F172A" : "#FFFFFF" }}>{n}</span>
      <h3 className="font-heading text-base font-bold text-[#0F172A]">{title}</h3>
      <p className="text-sm text-[#475569] leading-relaxed">{text}</p>
    </div>
  );
}
